import { useState, useContext, useRef, useEffect, useMemo } from "react";
import TicketsTable from "./Table";
import { useGetAllTickets, useGetTicketColumns } from "../../api/tickets";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useDebouncedValue } from "@mantine/hooks";
import { useSetActionButtons } from "../../services/ActionButtonsContext";
import { IconPlus } from "@tabler/icons-react";
import { useGetEvent } from "../../api/event";
import { Loader } from "@mantine/core";
import AttendeeLimitBanner from "../AttendeeLimitBanner";

function Tickets() {
  const tableRef = useRef();

  const handleAddNewTicket = () => {
    tableRef.current?.addNewRow();
  };

  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const { data: event } = useGetEvent(currentEventId);

  const [pagination, setPagination] = useState({ page: 1, limit: 50 });
  const [sorting, setSorting] = useState({
    columnAccessor: "firstName",
    direction: "asc",
  });
  const [filter, setFilter] = useState(null);
  const [debouncedFilter] = useDebouncedValue(filter, 200);

  const { data, isPending } = useGetAllTickets({
    eventId: currentEventId,
    ...pagination,
    sort: sorting,
    filter: debouncedFilter,
  });

  const {
    data: columnsData,
    isPending: columnsPending,
    isLoading: columnsLoading,
  } = useGetTicketColumns({
    eventId: currentEventId,
  });

  const customColumns = columnsData?.filter(
    (column) => column.isCustom && column.isVisible === true
  );

  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPagination({ ...pagination, page: 1 });
  };

  const actions = useMemo(
    () => [
      {
        label: "Create",
        onClick: handleAddNewTicket,
        icon: <IconPlus />,
        disabled: event?.isHardBlocked,
      },
    ],
    [event?.isHardBlocked]
  );

  useSetActionButtons(actions);

  // When changing an event, we need to reset pagination, filter and sorting
  useEffect(() => {
    setPagination({ page: 1, limit: 50 });
    setFilter(null);
    setSorting({ columnAccessor: "firstName", direction: "asc" });
  }, [currentEventId]);

  if (columnsPending || columnsLoading) {
    return <Loader />;
  }

  return (
    <>
      <AttendeeLimitBanner
        ticketsCount={event?.ticketsCount}
        attendeeLimit={event?.attendeeLimit}
        isAttendeeLimitReached={event?.isAttendeeLimitReached}
        isHardBlocked={event?.isHardBlocked}
      />
      <TicketsTable
        ref={tableRef}
        data={data}
        pagination={{ data: pagination, setPagination }}
        sorting={{ data: sorting, setSorting }}
        isPending={isPending}
        filter={{ data: filter, setFilter: onFilterChange(setFilter) }}
        customColumns={customColumns}
        columnsData={columnsData}
      />
    </>
  );
}

export default Tickets;
